/**
 * Frontend display helpers — keep internal trawler tags out of user-facing
 * surfaces, and turn publisher hostnames into readable source names.
 */

const INTERNAL_AI_REASONS = new Set(["tractrs-public", "tractrs-curated"]);

/**
 * Returns a renderable AI reason, or null when the value is an internal tag
 * that should NOT be shown to readers (e.g. "tractrs-public" — that's a
 * pipeline marker, not a sentence).
 */
export function frontendDisplayReason(reason: string | null | undefined): string | null {
  if (!reason) return null;
  if (INTERNAL_AI_REASONS.has(reason)) return null;
  return reason;
}

/**
 * Heuristic: does this string look like a domain/hostname rather than a
 * person/operator name? Used to decide whether to show "Operator" or "Source".
 */
export function looksLikeDomain(s: string | null | undefined): boolean {
  if (!s) return false;
  return /\.[a-z]{2,}(\.[a-z]{2,})?$/i.test(s.trim());
}

/**
 * Convert a publisher hostname to a readable name.
 *   theconstructionindex.co.uk → The Construction Index
 *   trucknews.com              → Trucknews
 *   nwparts.com                → Nwparts
 *
 * Best-effort — won't be perfect for short brand names, but won't ever look
 * worse than the raw hostname.
 */
export function humanizeSource(s: string | null | undefined): string | null {
  if (!s) return null;
  const cleaned = s
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\.(com|net|org|co|au|uk|us|ca|nz|io|tv|info|biz)(\.[a-z]{2})?$/i, "")
    .replace(/[-_.]+/g, " ")
    .trim();
  if (!cleaned) return null;
  // Title-case each word; preserve known initialisms like "uk", "us" as upper.
  return cleaned
    .split(/\s+/)
    .map((w) =>
      w.length <= 2 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    )
    .join(" ");
}

/**
 * Picks the right label for the source/operator — "Operator" for real names,
 * "Source" for publisher domains.
 */
export function sourceLabel(value: string | null | undefined): "Operator" | "Source" {
  return looksLikeDomain(value) ? "Source" : "Operator";
}

/**
 * Display value: humanised when the input looks like a domain, otherwise
 * passes through unchanged.
 */
export function displaySource(value: string | null | undefined): string | null {
  if (!value) return null;
  return looksLikeDomain(value) ? humanizeSource(value) : value;
}

/**
 * Return the first letter of a name as an avatar initial. Strips www. and
 * returns "?" when nothing usable.
 */
export function avatarInitial(name: string | null | undefined): string {
  if (!name) return "?";
  const cleaned = name.replace(/^www\./i, "").trim();
  return cleaned.charAt(0).toUpperCase() || "?";
}

/**
 * Format seconds as a duration badge: 75 → "1:15", 3725 → "1:02:05"
 */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || seconds <= 0) return null;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/**
 * Format kg as a compact human string: 9500 → "9.5 t", 850 → "850 kg".
 */
export function formatKg(kg: number | null | undefined): string | null {
  if (kg == null) return null;
  if (kg >= 1000) return `${(kg / 1000).toFixed(kg >= 10000 ? 0 : 1)} t`;
  return `${kg} kg`;
}

/**
 * Decode HTML entities in user-visible strings.
 *
 * Trawler sources (RSS, YouTube, scraped pages) often deliver titles like
 * "Hunton&#39;s Earthworks" — fine in HTML, broken when we render via React's
 * text path which escapes them as literal characters. Decode at the render
 * boundary so we don't have to backfill the DB.
 */
export function decodeHtml(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCharCode(parseInt(n, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, n: string) => String.fromCharCode(parseInt(n, 16)))
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    // Run &amp; last so it doesn't double-decode entities like "&amp;#39;"
    .replace(/&amp;/g, "&");
}
